'use client';

import { getTokenManager } from '@/legacy/utilities/tokenManager';
import { allDataClear, getCurrentOwnerData } from '@/model/dexie/ownerDexie';
import { getToken } from '@/model/dexie/userDexie';
import { getTabSync } from '@/utilities/tabSync';
import { useRouter } from 'next/navigation';
import { useEffect } from 'react';

/**
 * 탭 간 동기화를 처리하는 컴포넌트
 * 다른 탭에서 발생한 인증 관련 이벤트를 수신하고 처리
 */
const TabSyncHandler = () => {
	const router = useRouter();
	const tabSync = getTabSync();

	useEffect(() => {
		// 로그인 직후 세션 검증 스킵을 위한 상태
		let lastLoginTime = 0;
		let sessionValidationRetries = 0;
		const MAX_RETRIES = 2;
		const LOGIN_GRACE_PERIOD = 10000; // 로그인 후 10초간 검증 스킵
		// 로그인 이벤트 처리
		const handleLogin = async (message: any) => {
			console.log('[TabSyncHandler] Login event received from another tab');
			lastLoginTime = Date.now(); // 로그인 시간 기록
			sessionValidationRetries = 0; // 재시도 카운터 리셋
			// 현재 탭도 로그인 상태로 업데이트 (Dexie LiveQuery가 자동으로 처리)
		};

		// 로그아웃 이벤트 처리
		const handleLogout = async (message: any) => {
			console.log('[TabSyncHandler] Logout event received from another tab');
			// 다른 탭에서 로그아웃했으므로 현재 탭도 로그아웃 처리
			// clearUserData는 이미 호출되었으므로 UI만 업데이트
			const currentToken = await getToken();
			if (!currentToken) {
				// 현재 경로에서 locale 추출
				const currentPath = window.location.pathname;
				const pathSegments = currentPath.split('/').filter(Boolean);
				const possibleLocale = pathSegments[0];
				const isValidLocale = /^[a-z]{2}(-[A-Z]{2})?$/.test(possibleLocale);
				const locale = isValidLocale ? possibleLocale : 'ko';

				// 공개 페이지가 아니면 메인 페이지로 리다이렉트
				const publicPaths = [`/${locale}`, `/${locale}/login`, `/${locale}/signup`];
				const isPublicPath = publicPaths.some((path) => currentPath.startsWith(path)) || currentPath === '/';

				if (!isPublicPath) {
					router.push(`/${locale}`);
				}
			}
		};

		// 강제 로그아웃 이벤트 처리
		const handleForceLogout = async (message: any) => {
			console.log('[TabSyncHandler] Force logout event received from another tab');
			// 현재 경로에서 locale 추출
			const currentPath = window.location.pathname;
			const pathSegments = currentPath.split('/').filter(Boolean);
			const possibleLocale = pathSegments[0];
			const isValidLocale = /^[a-z]{2}(-[A-Z]{2})?$/.test(possibleLocale);
			const locale = isValidLocale ? possibleLocale : 'ko';

			// 모든 탭에서 강제 로그아웃 (깔끔하게)
			router.push(`/${locale}`);
		};

		// 계정 전환 이벤트 처리
		const handleAccountSwitch = async (message: any) => {
			console.log('[TabSyncHandler] Account switch event received from another tab');
			const { userId, user } = message.payload;
			console.log(`Account switched to: ${user?.nickname} (${userId})`);
			// Dexie LiveQuery가 자동으로 UI 업데이트를 처리
		};

		// 토큰 갱신 이벤트 처리
		const handleTokenRefresh = async (message: any) => {
			console.log('[TabSyncHandler] Token refresh event received from another tab');
			// tokenManager가 이미 처리했으므로 추가 작업 불필요
		};

		// 세션 충돌 감지 및 처리 (토큰 갱신 우선 시도)
		const handleSessionConflict = async () => {
			const currentUser = await getCurrentOwnerData();
			const currentToken = await getToken();

			// 로그인한 상태가 아니면 검증하지 않음
			if (!currentUser || !currentToken) {
				return;
			}

			// 로그인 직후라면 검증 스킵
			const timeSinceLogin = Date.now() - lastLoginTime;
			if (lastLoginTime > 0 && timeSinceLogin < LOGIN_GRACE_PERIOD) {
				console.log('[TabSyncHandler] Skipping session validation (within login grace period)');
				return;
			}

			try {
				const response = await fetch(process.env.NEXT_PUBLIC_STEPIN_AUTH + '/user', {
					headers: {
						accessToken: 'Bearer ' + currentToken.accessToken,
					},
				});

				if (!response.ok && response.status === 401) {
					console.log('[TabSyncHandler] Token appears expired, attempting refresh...');

					// 토큰 갱신 시도
					const tokenManager = getTokenManager();
					const refreshSuccess = await tokenManager.forceRefresh();

					if (refreshSuccess) {
						console.log('[TabSyncHandler] Token refreshed successfully');
						sessionValidationRetries = 0; // 성공시 카운터 리셋
						return;
					} else {
						sessionValidationRetries++;
						console.warn(`[TabSyncHandler] Token refresh failed (attempt ${sessionValidationRetries}/${MAX_RETRIES})`);

						// 최대 재시도 횟수에 도달한 경우에만 로그아웃
						if (sessionValidationRetries >= MAX_RETRIES) {
							console.warn('[TabSyncHandler] Max retries reached, logging out');
							await allDataClear();

							const currentPath = window.location.pathname;
							const pathSegments = currentPath.split('/').filter(Boolean);
							const possibleLocale = pathSegments[0];
							const isValidLocale = /^[a-z]{2}(-[A-Z]{2})?$/.test(possibleLocale);
							const locale = isValidLocale ? possibleLocale : 'ko';

							router.push(`/${locale}`);
						}
					}
				} else if (response.ok) {
					// 검증 성공시 재시도 카운터 리셋
					sessionValidationRetries = 0;
				}
			} catch (error) {
				console.error('[TabSyncHandler] Session validation error:', error);
				// 네트워크 에러는 무시 (오프라인 상황 등)
			}
		};

		// 이벤트 리스너 등록
		tabSync.on('LOGIN', handleLogin);
		tabSync.on('LOGOUT', handleLogout);
		tabSync.on('FORCE_LOGOUT', handleForceLogout);
		tabSync.on('ACCOUNT_SWITCH', handleAccountSwitch);
		tabSync.on('TOKEN_REFRESH', handleTokenRefresh);

		// 주기적 세션 검증 (5분마다)
		const sessionCheckInterval = setInterval(handleSessionConflict, 5 * 60 * 1000);

		// Visibility API를 사용한 탭 활성화 감지
		const handleVisibilityChange = () => {
			if (!document.hidden) {
				// 탭이 다시 활성화되었을 때 세션 상태 확인
				setTimeout(handleSessionConflict, 1000);
			}
		};

		document.addEventListener('visibilitychange', handleVisibilityChange);

		// 정리 함수
		return () => {
			tabSync.off('LOGIN', handleLogin);
			tabSync.off('LOGOUT', handleLogout);
			tabSync.off('FORCE_LOGOUT', handleForceLogout);
			tabSync.off('ACCOUNT_SWITCH', handleAccountSwitch);
			tabSync.off('TOKEN_REFRESH', handleTokenRefresh);

			clearInterval(sessionCheckInterval);
			document.removeEventListener('visibilitychange', handleVisibilityChange);
		};
	}, [router, tabSync]);

	// 렌더링할 UI 없음 (백그라운드 처리만)
	return null;
};

export default TabSyncHandler;
